import { Geolocation } from '@capacitor/geolocation';
import { isNative } from './capacitor';

/**
 * Returns { lat, lng } or null if denied / unavailable.
 */
export async function getCurrentPosition() {
  try {
    const { coords } = await Geolocation.getCurrentPosition({
      enableHighAccuracy: false,
      timeout: 10000,
    });
    return { lat: coords.latitude, lng: coords.longitude };
  } catch {
    return null;
  }
}

/**
 * Returns 'granted' | 'denied' | 'prompt' without triggering a system prompt.
 */
export async function checkPermission() {
  if (!isNative()) {
    // Web: use the Permissions API if available
    try {
      const status = await navigator.permissions.query({ name: 'geolocation' });
      return status.state; // 'granted' | 'denied' | 'prompt'
    } catch {
      return 'prompt';
    }
  }
  try {
    const { location } = await Geolocation.checkPermissions();
    return location;
  } catch {
    return 'denied';
  }
}

/**
 * Triggers the system / browser location permission prompt.
 * Returns the new status: 'granted' | 'denied' | 'prompt'
 */
export async function requestLocationPermission() {
  if (!isNative()) {
    // Web: the only way to trigger the browser prompt is to call
    // getCurrentPosition() — requestPermissions() is a no-op on web.
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        () => resolve('granted'),
        (err) => resolve(err.code === 1 ? 'denied' : 'prompt'),
        { timeout: 15000 }
      );
    });
  }
  // Native (Android / iOS): use Capacitor's permission request
  try {
    const { location } = await Geolocation.requestPermissions();
    return location;
  } catch {
    return 'denied';
  }
}
